import React, { useEffect, useRef } from 'react'
import type { ProductionOrder } from '@/services/productionOrders'

export interface ProductionOrderRowMenuProps {
  row: ProductionOrder
  canSalesWrite: boolean
  canDemandWrite: boolean
  isOpen: boolean
  anchorRect: DOMRect | null
  onClose: () => void
  onViewHistory: () => void
  onToggleUnit: () => void
  onReturnToDraft: () => void
  onPushToDemand: () => void
  onOpenProductionDemand?: () => void
  onOpenExecution?: () => void
}

export function ProductionOrderRowMenu({
  row,
  canSalesWrite,
  canDemandWrite,
  isOpen,
  anchorRect,
  onClose,
  onViewHistory,
  onToggleUnit,
  onReturnToDraft,
  onPushToDemand,
  onOpenProductionDemand,
  onOpenExecution,
}: ProductionOrderRowMenuProps): React.ReactElement | null {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen || !anchorRect) return null

  // Calculate fixed position
  const menuWidth = 240
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800

  let left = anchorRect.left - menuWidth + 24
  if (left + menuWidth > viewportWidth - 16) {
    left = viewportWidth - menuWidth - 16
  }
  if (left < 16) left = 16

  let top = anchorRect.bottom + 4
  const estimatedHeight = 260
  if (top + estimatedHeight > viewportHeight - 16) {
    top = Math.max(16, anchorRect.top - estimatedHeight - 4)
  }

  const currentUnit = row.dim_unit || 'inch'
  const nextUnit = currentUnit === 'inch' ? 'mm' : 'inch'

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${menuWidth}px`,
        zIndex: 999999,
      }}
      className="rounded-lg border border-slate-200 bg-white py-1.5 shadow-2xl animate-in fade-in zoom-in-95 duration-100 text-sm select-none"
    >
      <div className="border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500 font-mono truncate">
        {row.variant_code || 'Dòng kế hoạch'}
      </div>

      {/* 1. Xem lịch sử dòng */}
      <button
        type="button"
        className="w-full px-3 py-2 text-left font-medium text-slate-700 hover:bg-slate-100 transition-colors flex items-center justify-between"
        onClick={() => {
          onViewHistory()
          onClose()
        }}
      >
        <span>Xem lịch sử dòng</span>
      </button>

      {/* 2. Đổi đơn vị hiển thị MM / INCH */}
      <button
        type="button"
        className="w-full px-3 py-2 text-left font-medium text-slate-700 hover:bg-slate-100 transition-colors flex items-center justify-between"
        onClick={() => {
          onToggleUnit()
          onClose()
        }}
      >
        <span>Đổi sang {nextUnit.toUpperCase()}</span>
        <span className="text-xs text-slate-400 font-mono">({currentUnit})</span>
      </button>

      <div className="my-1 border-t border-slate-100" />

      {/* 3. Trả về Nháp */}
      {canSalesWrite ? (
        <button
          type="button"
          className="w-full px-3 py-2 text-left font-medium text-amber-700 hover:bg-amber-50 transition-colors flex items-center justify-between"
          onClick={() => {
            onReturnToDraft()
            onClose()
          }}
        >
          <span>Trả về Nháp</span>
        </button>
      ) : (
        <div
          className="px-3 py-1.5 text-xs text-slate-400 italic cursor-not-allowed"
          title="Cần quyền sales.write để chuyển trạng thái dòng đơn"
        >
          Trả về Nháp (cần sales.write)
        </div>
      )}

      {/* 4. Đưa vào nhu cầu vật tư */}
      {canDemandWrite ? (
        <button
          type="button"
          className="w-full px-3 py-2 text-left font-medium text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-between"
          onClick={() => {
            onPushToDemand()
            onClose()
          }}
        >
          <span>Đưa vào nhu cầu vật tư</span>
        </button>
      ) : (
        <div
          className="px-3 py-1.5 text-xs text-slate-400 italic cursor-not-allowed"
          title="Cần quyền demand.write để đẩy sang Nhu cầu"
        >
          Đưa vào nhu cầu (cần demand.write)
        </div>
      )}

      {/* 4b. Nhu cầu & Giữ vật tư (BOM Reservation) */}
      <button
        type="button"
        className="w-full px-3 py-2 text-left font-medium text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-between"
        onClick={() => {
          onOpenProductionDemand?.()
          onClose()
        }}
      >
        <span>Nhu cầu & Giữ vật tư</span>
      </button>

      <button
        type="button"
        className="w-full px-3 py-2 text-left font-medium text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-between"
        onClick={() => {
          onOpenExecution?.()
          onClose()
        }}
      >
        <span>Thực hiện sản xuất</span>
      </button>

      <div className="my-1 border-t border-slate-100" />

      {/* 5. Tách dòng (Chưa làm) */}
      <div className="px-3 py-2 text-slate-400 flex items-center justify-between cursor-not-allowed">
        <span>Tách dòng</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-500">
          Chưa làm
        </span>
      </div>

      {/* 6. Chuyển tháng (Chưa làm) */}
      <div className="px-3 py-2 text-slate-400 flex items-center justify-between cursor-not-allowed">
        <span>Chuyển tháng</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-500">
          Chưa làm
        </span>
      </div>
    </div>
  )
}
